import React, { useState } from 'react';
import { Tooltip, Spin } from 'antd';
import questionIcon from '../../Assets/schedule/question.svg';
import publishIcon from '../../Assets/schedule/publish.svg';
import unpublishIcon from '../../Assets/schedule/unpublish.svg';
import deleteIcon from '../../Assets/schedule/delete.svg';
import emptyImage from '../../Assets/schedule/Group 10647.svg';
import SavingModal from '../../components/SavingModal';

/* ================= ACTION BUTTON ================= */
const ActionButton = ({ icon, onClick, title }) => (
  <Tooltip title={title} overlayStyle={{ zIndex: 100000 }}>
    <button className="quiz-action-btn" onClick={onClick}>
      <img src={icon} alt={title || "action"} width={20} height={20} />
    </button>
  </Tooltip>
);

/* ================= QUIZ ITEM ================= */
const QuizItem = ({
  id,
  title,
  questionsCount,
  duration,
  isPublished = true,
  isReadOnly,
  onEdit,
  onPublish,
  onUnpublish,
  setModalConfig,
  setModalVisible,
  confirmDeleteQuiz,
  ...props
}) => {
  const [published, setPublished] = useState(isPublished);

  // Sync state if backend updates
  React.useEffect(() => {
    setPublished(isPublished);
  }, [isPublished]);

  const getMetadata = () => {
    let meta = `${questionsCount} ${questionsCount === 1 ? 'question' : 'questions'}`;
    const time = duration || props.timeBound || props.timebound;
    if (time) meta += ` | ${time} ${Number(time) === 1 ? 'min' : 'mins'}`;
    if (props.isGraded || props.graded) meta += ` | Graded`;
    return meta;
  };

  return (
    <div className="quiz-resource-item">
      <div className="quiz-resource-content">
        <div className="quiz-icon-wrapper">
          <img src={questionIcon} alt="quiz" width={24} height={24} />
        </div>

        <div 
          className="quiz-resource-info" 
          onClick={() => !isReadOnly && onEdit?.({ id, title, questionsCount, duration, isPublished, ...props })}
          style={{ cursor: !isReadOnly ? 'pointer' : 'default' }}
        >
          {/* zIndex 100000 - this panel lives inside ScheduleDetailsDrawer (z 99999),
              and antd's default tooltip z-index of 1070 puts the overlay behind it. */}
          <Tooltip
            title={title}
            placement="topLeft"
            mouseEnterDelay={0.3}
            overlayStyle={{ zIndex: 100000, maxWidth: 420 }}
          >
            <h4 className="quiz-resource-title">{title}</h4>
          </Tooltip>
          <p className="quiz-resource-meta">{getMetadata()}</p>
        </div>
      </div>

      <div className="quiz-resource-actions">
        {!isReadOnly && (
          <>
        <ActionButton
          icon={published ? unpublishIcon : publishIcon}
          title={published ? "Unpublish" : "Publish"}
          onClick={() => {
            const newState = !published;
            setModalConfig({
              title: 'Confirm',
              message: `Are you sure you want to ${newState ? 'publish' : 'unpublish'} the quiz?`,
              onConfirm: () => {
                setPublished(newState);
                newState ? onPublish?.(id) : onUnpublish?.(id);
                setModalVisible(false);
              }
            });
            setModalVisible(true);
          }}
        />

        <ActionButton
          icon={deleteIcon}
          title="Delete"
          onClick={() => confirmDeleteQuiz(id)}
        />
          </>
        )}
      </div>
    </div>
  );
};

const QuizzesPanel = ({
  scheduleId,
  quizzes = [],
  loading = false,
  onDeleteAll,
  onAddQuiz,
  onPublish,
  onUnpublish,
  onDelete,
  onEdit,
  isReadOnly,
}) => {
  const hasQuizzes = quizzes.length > 0;
  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: '', message: '', onConfirm: () => { } });

  const confirmDeleteQuiz = (id) => {
    setModalConfig({
      title: 'Confirm',
      message: 'Are you sure you want to delete the quiz?',
      onConfirm: () => {
        onDelete?.(id);
        setModalVisible(false);
      }
    });
    setModalVisible(true);
  };

  const confirmDeleteAll = () => {
    setModalConfig({
      title: 'Confirm',
      message: 'Are you sure you want to delete all quizzes?',
      onConfirm: () => {
        onDeleteAll?.();
        setModalVisible(false);
      }
    });
    setModalVisible(true);
  };



  return (
    <div className="quizzes-panel">
      <style>{`
        .quizzes-panel {
          font-family: 'Roboto', sans-serif;
          background: #fff;
          width: 100%;
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: calc(100vh - 180px);
        }

        .quizzes-content {
          padding: 24px 32px;
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          overflow-x: hidden;
          justify-content: ${loading || !hasQuizzes ? 'center' : 'flex-start'};
        }

        .quizzes-section-title {
          font-family: 'Roboto', sans-serif;
          font-size: 12px;
          color: #636363;
          margin-bottom: 20px;
          font-weight: 400;
          line-height: 100%;
        }

        .quiz-resource-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          border: 1px solid #e6e6e6;
          border-radius: 12px;
          margin-bottom: 16px;
          transition: all 0.2s ease;
        }

        .quiz-resource-item:hover {
          border-color: #594099;
          box-shadow: 0 2px 8px rgba(89, 64, 153, 0.1);
        }

        .quiz-resource-content {
          display: flex;
          align-items: center;
          gap: 16px;
          flex: 1;
          min-width: 0;
        }

        .quiz-resource-info {
           flex: 1;
           min-width: 0;
        }

        .quiz-icon-wrapper {
          width: 48px;
          height: 48px;
          background: #f5f3f9;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .quiz-resource-title {
          font-size: 14px;
          font-weight: 600;
          color: #191919;
          margin: 0 0 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
          display: block;
        }

        .quiz-resource-meta {
          font-size: 12px;
          color: #636363;
          margin: 0;
        }

        .quiz-resource-actions {
          display: flex;
          gap: 12px;
        }

        .quiz-action-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s;
        }

        .quiz-action-btn:hover {
          transform: scale(1.1);
        }

        .quiz-btn-row {
          display: flex;
          gap: 16px;
          padding: 24px 32px;
          background: #fff;
          box-shadow: 0 -2px 8px rgba(0,0,0,0.06);
          flex-shrink: 0;
          z-index: 10;
        }

        .quiz-btn {
          flex: 1;
          height: 48px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          padding: 0 16px;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          transition: all 0.2s;
        }

        .quiz-btn-delete {
          background: #FFFFFF;
          color: #FF4D4F;
          border: 1px solid #FF4D4F;
        }

        .quiz-btn-delete:hover {
          background: #FFF2F0;
        }

        .quiz-btn-add {
          background: #594099;
          color: #FFFFFF;
          border: none;
        }

        .quiz-btn-add:hover {
          background: #4A3580;
          box-shadow: 0 4px 12px rgba(89, 64, 153, 0.2);
        }

        .no-quizzes {
          text-align: center;
          padding: 40px 0;
          color: #636363;
          font-size: 14px;
        }
      `}</style>

      <div className="quizzes-content">
        {loading ? (
          // Quizzes are still being (re)fetched for this schedule - show a loader
          // instead of whatever's currently in `quizzes` (which, while a fetch is in
          // flight, can still be the previous schedule's list for a moment). Rendering
          // that stale list would flash real-looking quizzes before the fresh - often
          // empty - result replaces them, instead of just holding on a loader.
          <div style={{ textAlign: 'center', padding: '60px 0', width: '100%' }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            {hasQuizzes && <div className="quizzes-section-title">Quizzes</div>}
            <div className="quizzes-list">
              {hasQuizzes ? (
                quizzes.map((quiz) => (
                  <QuizItem 
                    key={quiz.id} 
                    {...quiz} 
                    isReadOnly={isReadOnly}
                    onEdit={onEdit}
                    onPublish={onPublish}
                    onUnpublish={onUnpublish}
                    setModalConfig={setModalConfig}
                    setModalVisible={setModalVisible}
                    confirmDeleteQuiz={confirmDeleteQuiz}
                  />
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '20px 0', color: '#636363' }}>
                  <img
                    src={emptyImage}
                    alt="No quizzes"
                    style={{ width: '220px', marginBottom: '24px' }}
                  />
                  <div style={{ fontSize: '14px', fontWeight: 500 }}>
                    No quizzes added yet.
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {!isReadOnly && (
        <div className="quiz-btn-row" style={!hasQuizzes ? { justifyContent: 'center' } : {}}>
          {hasQuizzes && (
            <button
              className="quiz-btn quiz-btn-delete"
              onClick={confirmDeleteAll}
              style={{
                background: '#FFFFFF',
                color: '#FF414D',
                border: '1px solid #FF414D'
              }}
            >
              DELETE ALL QUIZZES
            </button>
          )}
          <button
            className="quiz-btn quiz-btn-add"
            onClick={onAddQuiz}
            style={!hasQuizzes ? { flex: 'none', width: '200px' } : {}}
          >
            ADD A QUIZ
          </button>
        </div>
      )}

      <SavingModal
        visible={modalVisible}
        title={modalConfig.title}
        message={modalConfig.message}
        onConfirm={modalConfig.onConfirm}
        onCancel={() => setModalVisible(false)}
      />
    </div>
  );
};

export default QuizzesPanel;
